import { useEffect, useRef } from 'react';
import { drawStroke, drawStrokes, simplifyPoints } from '../drawing/render';
import { uid } from '../lib/id';
import type { Stroke, Tool } from '../storage/schemas';

interface Props {
  strokes: Stroke[];
  active: boolean;
  tool: Tool;
  color: string;
  /** Brush size as a fraction of the card width. */
  size: number;
  onStroke: (s: Stroke) => void;
}

/**
 * Drawing layer. Committed strokes are painted on one canvas; the stroke in progress on a second one so we
 * never repaint everything per pointer event. `touch-action: none` applies only while drawing.
 */
export function DrawingSurface({ strokes, active, tool, color, size, onStroke }: Props) {
  const base = useRef<HTMLCanvasElement>(null);
  const live = useRef<HTMLCanvasElement>(null);
  const current = useRef<{ id: number; points: number[] } | null>(null);
  const penSeen = useRef(false);

  const sizeCanvas = (c: HTMLCanvasElement) => {
    const rect = c.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round(rect.width * dpr);
    const h = Math.round(rect.height * dpr);
    if (c.width !== w || c.height !== h) {
      c.width = w;
      c.height = h;
    }
    const ctx = c.getContext('2d');
    ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { ctx, width: rect.width, height: rect.height };
  };

  useEffect(() => {
    const c = base.current;
    if (!c) return;
    const paint = () => {
      const { ctx, width, height } = sizeCanvas(c);
      if (!ctx || !width) return;
      ctx.clearRect(0, 0, width, height);
      drawStrokes(ctx, strokes, width);
    };
    paint();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(paint) : null;
    ro?.observe(c);
    return () => ro?.disconnect();
  }, [strokes]);

  const point = (e: React.PointerEvent) => {
    const r = live.current!.getBoundingClientRect();
    const pressure = e.pointerType === 'pen' ? e.pressure || 0.5 : 0.5;
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.width, Math.round(pressure * 100) / 100];
  };

  const redrawLive = () => {
    const c = live.current;
    const cur = current.current;
    if (!c || !cur) return;
    const { ctx, width, height } = sizeCanvas(c);
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    if (tool === 'eraser') {
      // Preview the erase on the live layer by showing what remains.
      drawStrokes(ctx, strokes, width);
      drawStroke(ctx, { id: 'live', tool, color, size, points: cur.points }, width);
      base.current!.style.visibility = 'hidden';
    } else drawStroke(ctx, { id: 'live', tool, color, size, points: cur.points }, width);
  };

  return (
    <>
      <canvas ref={base} className="nc-strokes" aria-hidden="true" />
      <canvas
        ref={live}
        className={`nc-strokes nc-draw-input ${active ? 'is-active' : ''}`}
        aria-label={active ? 'Drawing area. Draw with a finger, stylus or mouse.' : undefined}
        role={active ? 'img' : undefined}
        onPointerDown={(e) => {
          if (!active) return;
          if (e.pointerType === 'pen') penSeen.current = true;
          // Palm rejection: once a stylus has been used, ignore touch input.
          if (e.pointerType === 'touch' && penSeen.current) return;
          if (current.current) return; // one stroke at a time
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          current.current = { id: e.pointerId, points: point(e) };
          redrawLive();
        }}
        onPointerMove={(e) => {
          const cur = current.current;
          if (!cur || cur.id !== e.pointerId) return;
          const events = (e.nativeEvent as PointerEvent).getCoalescedEvents?.() ?? [e.nativeEvent];
          const r = live.current!.getBoundingClientRect();
          for (const ev of events) {
            const pressure = ev.pointerType === 'pen' ? ev.pressure || 0.5 : 0.5;
            cur.points.push((ev.clientX - r.left) / r.width, (ev.clientY - r.top) / r.width, Math.round(pressure * 100) / 100);
          }
          redrawLive();
        }}
        onPointerUp={(e) => {
          const cur = current.current;
          if (!cur || cur.id !== e.pointerId) return;
          current.current = null;
          const c = live.current!;
          c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
          base.current!.style.visibility = '';
          if (cur.points.length >= 3) onStroke({ id: uid('s'), tool, color, size, points: simplifyPoints(cur.points) });
        }}
        onPointerCancel={() => {
          current.current = null;
          const c = live.current!;
          c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
          base.current!.style.visibility = '';
        }}
      />
    </>
  );
}
